// Promise-based prompt dialog. Replaces window.prompt() and the inline
// rename forms that used to live in the collection sidebar.
//
// Usage:
//   const prompt = usePrompt();
//   const name = await prompt({ title: "Rename collection", initialValue: c.name });
//   if (name) rename.mutate(name);

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { LoadingButton } from "./Spinner";

export interface PromptOpts {
  title: string;
  description?: ReactNode;
  initialValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** A regex the value must match (e.g. /^#[0-9a-fA-F]{6}$/). */
  pattern?: RegExp;
  /** Synchronous validator. Returns an error message or null. */
  validate?: (v: string) => string | null;
  /** Allow submitting an empty string. Default false. */
  allowEmpty?: boolean;
  /** Show in a wider editor (multiline textarea). */
  multiline?: boolean;
  /** Label for the input field; defaults to the title. */
  inputLabel?: string;
  /** Force the input type. Default "text". */
  type?: "text" | "url" | "email";
}

const PromptContext = createContext<((opts: PromptOpts) => Promise<string | null>) | null>(null);

export function usePrompt(): (opts: PromptOpts) => Promise<string | null> {
  const fn = useContext(PromptContext);
  if (!fn) throw new Error("usePrompt: missing <PromptProvider>");
  return fn;
}

export function PromptProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ opts: PromptOpts; resolve: (v: string | null) => void } | null>(null);

  const prompt = useCallback(
    (opts: PromptOpts) => new Promise<string | null>((resolve) => setState({ opts, resolve })),
    []
  );

  const close = useCallback((value: string | null) => {
    if (state) {
      state.resolve(value);
      setState(null);
    }
  }, [state]);

  return (
    <PromptContext.Provider value={prompt}>
      {children}
      {state && <PromptDialog opts={state.opts} onClose={close} />}
    </PromptContext.Provider>
  );
}

function PromptDialog({ opts, onClose }: { opts: PromptOpts; onClose: (v: string | null) => void }) {
  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const [value, setValue] = useState(opts.initialValue ?? "");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    // Focus + select-all on open so users can immediately start typing or
    // delete-and-retype.
    const el = inputRef.current;
    if (el) { el.focus(); if (typeof el.select === "function") el.select(); }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(null); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const validate = (v: string): string | null => {
    if (!opts.allowEmpty && !v.trim()) return "Required.";
    if (opts.pattern && !opts.pattern.test(v)) return "Invalid format.";
    if (opts.validate) return opts.validate(v);
    return null;
  };

  const submit = () => {
    const v = value;
    const e = validate(v);
    if (e) { setErr(e); return; }
    onClose(v);
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4"
      style={{ background: "var(--backdrop)" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="prompt-title"
      onClick={() => onClose(null)}
    >
      <form
        className="card w-full max-w-md p-5 shadow-2xl space-y-3"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); submit(); }}
      >
        <h2 id="prompt-title" className="text-lg font-semibold">{opts.title}</h2>
        {opts.description && (
          <div className="text-sm muted">{opts.description}</div>
        )}
        <div className="space-y-1">
          {opts.inputLabel && <label className="label">{opts.inputLabel}</label>}
          {opts.multiline ? (
            <textarea
              ref={inputRef}
              className="input input-mono min-h-[120px]"
              value={value}
              placeholder={opts.placeholder}
              onChange={(e) => { setValue(e.target.value); if (err) setErr(null); }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); }
              }}
            />
          ) : (
            <input
              ref={inputRef as unknown as React.RefObject<HTMLInputElement>}
              className="input"
              type={opts.type ?? "text"}
              value={value}
              placeholder={opts.placeholder}
              onChange={(e) => { setValue(e.target.value); if (err) setErr(null); }}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
            />
          )}
          {err && <p className="text-xs text-danger">{err}</p>}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn" onClick={() => onClose(null)}>
            {opts.cancelLabel ?? "Cancel"}
          </button>
          <LoadingButton
            type="submit"
            className="btn-primary"
            pending={false}
          >
            {opts.confirmLabel ?? "OK"}
          </LoadingButton>
        </div>
      </form>
    </div>
  );
}
